import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import { tagged } from "@mirage/service-logging";
import { v4 as uuidv4 } from "uuid";
import type { SessionStorage } from "./types";

const logger = tagged("service-sessions");

export type Service = ReturnType<typeof service>;

export default function service(adapter: SessionStorage) {
  // Clear existing sessions on service instantiation
  clearSessionIds().catch((error) => {
    logger.error("Failed to clear sessions on service instantiation:", error);
  });

  async function createOrGetSessionId(
    type: string,
    ownerId: string,
  ): Promise<string> {
    const existingSession = await adapter.get(type);
    if (existingSession) {
      // Use Set to ensure unique owners
      const uniqueOwners = new Set([...existingSession.owners, ownerId]);
      await adapter.set(type, {
        ...existingSession,
        owners: Array.from(uniqueOwners),
      });
      logger.debug("Using existing session id -", type, existingSession.id);
      return existingSession.id;
    }

    const newSessionId = uuidv4();
    await adapter.set(type, { id: newSessionId, owners: [ownerId] });
    logger.debug("Created new session id -", type, newSessionId);
    return newSessionId;
  }

  async function deleteSessionId(type: string, ownerId: string): Promise<void> {
    const existingSession = await adapter.get(type);
    if (!existingSession) {
      logger.error("Deleting session that does not exist", type);
      return;
    }

    // Use Set to ensure unique owners when removing
    const uniqueOwners = new Set(existingSession.owners);
    uniqueOwners.delete(ownerId);

    if (uniqueOwners.size === 0) {
      await adapter.delete(type);
      logger.debug("Deleted session -", type, existingSession.id);
    } else {
      await adapter.set(type, {
        ...existingSession,
        owners: Array.from(uniqueOwners),
      });
      logger.debug("Removed owner from session -", type, existingSession.id);
    }
  }

  function clearSessionIds(): Promise<void> {
    return adapter.clear();
  }

  return services.provide(
    ServiceId.SESSIONS,
    {
      createOrGetSessionId,
      deleteSessionId,
      clearSessionIds,
    },
    [],
  );
}
